import type { DashboardFilters } from '../forecasting.types';
import { isBookedStatus, isForecastStatus, isPipeStatus } from './monthlyStageRules';

export const OPTIONAL_FILTER_KEYS = [
  'channel',
  'sales',
  'buyingPoint',
  'am',
  'advertiser',
  'status',
  'source',
  'clientType',
  'newExisting',
  'territory',
  'platform'
] as const satisfies readonly (keyof DashboardFilters)[];

export type OptionalFilterKey = (typeof OPTIONAL_FILTER_KEYS)[number];

export function clearOptionalFiltersPatch(): Partial<DashboardFilters> {
  return Object.fromEntries(OPTIONAL_FILTER_KEYS.map((key) => [key, undefined])) as Partial<DashboardFilters>;
}

export function hasOptionalFiltersActive(filters: DashboardFilters): boolean {
  return OPTIONAL_FILTER_KEYS.some((key) => Boolean(filters[key]?.length));
}

export function isDateRangeCustom(
  filters: DashboardFilters,
  defaultFrom: string,
  defaultTo: string
): boolean {
  return filters.dateFrom !== defaultFrom || filters.dateTo !== defaultTo;
}

export function hasActiveSidebarFilters(
  filters: DashboardFilters,
  defaults: Pick<DashboardFilters, 'dateFrom' | 'dateTo'>
): boolean {
  return hasOptionalFiltersActive(filters) || isDateRangeCustom(filters, defaults.dateFrom, defaults.dateTo);
}

/* Statuses the figures count: booked and pipe, the forecast, and the goal rows
   (Budgetv2 on the full-year page, Target on the quarterly). */
const COUNTED_GOAL_STATUSES = ['budgetv2', 'target'];

/**
 * The picked statuses no figure on the page counts, e.g. "50% - Proposal Sent".
 * Filtering to only these empties every card and chart, which read as broken,
 * so the page names them instead.
 */
export function uncountedStatuses(statuses: string[] | undefined): string[] {
  return (statuses ?? []).filter((status) => {
    const t = status.trim().toLowerCase();
    return !(
      isBookedStatus(status) ||
      isPipeStatus(status) ||
      isForecastStatus(status) ||
      COUNTED_GOAL_STATUSES.includes(t)
    );
  });
}
